import { useCallback, useMemo, useState } from 'react'
import { useServerLogsHub } from '../../hooks/useSignalR'
import type { MachineLog, MachineLogType } from '../../types'
import { LOG_TYPES, TYPE_LABELS, formatTime } from './shared'
import { Chip, TypeBadge } from './ui'

// 실시간: 로그를 쌓지 않고 (종류, 설비, 코드) 별 최신값 한 줄만 유지 — 값·시각만 제자리 갱신
interface LatestValue {
  log: MachineLog
  rev: number // 갱신 횟수 — 셀 key 로 써서 값이 바뀔 때마다 하이라이트를 다시 재생
}

const latestKey = (log: MachineLog) => `${log.type}|${log.machineCode}|${log.code}`

function LatestTable({ rows, empty, showMachine }: { rows: LatestValue[]; empty: string; showMachine: boolean }) {
  const cols = showMachine ? 5 : 4
  return (
    <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden flex flex-col min-h-0">
      <div className="min-h-0 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
            <tr>
              {showMachine && <th className="text-left px-4 py-3">설비</th>}
              <th className="text-left px-4 py-3">코드</th>
              <th className="text-left px-4 py-3 w-28">종류</th>
              <th className="text-left px-4 py-3">값</th>
              <th className="text-left px-4 py-3 w-48">시각</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={cols} className="px-4 py-8 text-center text-gray-400 text-sm">{empty}</td>
              </tr>
            ) : (
              rows.map(({ log, rev }) => (
                <tr key={latestKey(log)} className="hover:bg-gray-50">
                  {showMachine && <td className="px-4 py-2.5 font-medium text-gray-800">{log.machineCode}</td>}
                  <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{log.code}</td>
                  <td className="px-4 py-2.5"><TypeBadge type={log.type} /></td>
                  <td key={rev} className="px-4 py-2.5 text-gray-700 animate-[value-flash_1.2s_ease-out]">{log.value}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs font-mono whitespace-nowrap">{formatTime(log.collectedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// machineCode 를 주면 그 설비로 한정하고 설비 필터·열을 숨긴다 (마스터데이터에서 설비를 고른 경우)
export default function LiveView({ serverId, machineCode }: { serverId: string; machineCode?: string }) {
  const [latest, setLatest] = useState<Record<string, LatestValue>>({})
  const [typeFilter, setTypeFilter] = useState<MachineLogType | null>(null)
  const [machineFilter, setMachineFilter] = useState('')

  const onLogs = useCallback((logs: MachineLog[]) => {
    setLatest(prev => {
      const next = { ...prev }
      for (const log of logs) {
        const key = latestKey(log)
        const cur = next[key]
        // 배치 안·배치 간 순서가 뒤섞여도 수집 시각이 더 최신일 때만 교체
        if (cur && Date.parse(cur.log.collectedAt) > Date.parse(log.collectedAt)) continue
        next[key] = { log, rev: (cur?.rev ?? 0) + 1 }
      }
      return next
    })
  }, [])

  useServerLogsHub(serverId, onLogs)

  // 설비 → 코드 순 고정 정렬 — 값이 갱신돼도 행 위치가 움직이지 않게
  const filtered = useMemo(() => Object.values(latest)
    .filter(({ log }) => {
      if (machineCode && log.machineCode !== machineCode) return false
      if (typeFilter && log.type !== typeFilter) return false
      if (!machineCode && machineFilter && !log.machineCode.toLowerCase().includes(machineFilter.toLowerCase())) return false
      return true
    })
    .sort((a, b) =>
      a.log.machineCode.localeCompare(b.log.machineCode)
      || a.log.code.localeCompare(b.log.code)
      || a.log.type.localeCompare(b.log.type)),
  [latest, typeFilter, machineFilter, machineCode])

  // 필터 줄은 고정하고 표만 스크롤 (thead 고정)
  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-wrap items-center gap-3 mb-4 shrink-0">
        <div className="flex flex-wrap gap-1.5">
          <Chip active={typeFilter === null} onClick={() => setTypeFilter(null)}>전체</Chip>
          {LOG_TYPES.map(t => (
            <Chip key={t} active={typeFilter === t} onClick={() => setTypeFilter(t)}>{TYPE_LABELS[t]}</Chip>
          ))}
        </div>
        {!machineCode && (
          <input
            value={machineFilter}
            onChange={e => setMachineFilter(e.target.value)}
            placeholder="설비코드 필터"
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-40"
          />
        )}
        <span className="text-xs text-gray-400 ml-auto">
          항목 {filtered.length}개
        </span>
      </div>
      <LatestTable rows={filtered} empty="아직 수신된 값이 없습니다." showMachine={!machineCode} />
    </div>
  )
}
